// Config
import { CONFIG } from "@/app/config";

// Constants
import { QUERY_LIMIT_SINGLE } from "@benchmark/core/constants";

// Context
import { useCurrentUserOptional } from "@/app/auth/CurrentUserContext";

// Hooks
import { useEffect, useState, useCallback } from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";

// Types
import type { Practitioner, PractitionerRole } from "@medplum/fhirtypes";

/** Result from useCurrentPractitionerRole hook. @agentic @benchmark/core */
export type UseCurrentPractitionerRoleResult = {
  profile: Practitioner | undefined;
  practitionerRole: PractitionerRole | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

/**
 * Returns the current Medplum profile enriched with the user's primary active
 * PractitionerRole.
 *
 * When rendered under `<CurrentUserProvider>` (the authenticated app tree) this
 * delegates to the shared context, so the role set is fetched once and reused
 * everywhere. Outside the provider it falls back to fetching its own role,
 * preserving the original standalone behavior.
 * @agentic @benchmark/core
 */
export function useCurrentPractitionerRole(): UseCurrentPractitionerRoleResult {
  const currentUser = useCurrentUserOptional();

  const fallback = useCurrentPractitionerRoleFallback({
    enabled: currentUser === null,
  });

  if (currentUser) {
    return {
      profile: currentUser.practitioner,
      practitionerRole: currentUser.primaryPractitionerRole,
      isLoading: currentUser.isLoading,
      error: currentUser.error,
      refetch: currentUser.refetch,
    };
  }

  return fallback;
}

/**
 * Standalone fetch of the current user's primary Benchmark PractitionerRole.
 * Used when no `<CurrentUserProvider>` is present. The fetch is skipped when
 * `enabled` is false so the provider-backed path does no redundant work.
 * @agentic @benchmark/core
 */
function useCurrentPractitionerRoleFallback({
  enabled,
}: {
  enabled: boolean;
}): UseCurrentPractitionerRoleResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;

  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRole = useCallback(async () => {
    if (!profile?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const benchmarkOrgId = CONFIG.VITE_BENCHMARK_ORGANIZATION_ID;
      const searchParams: Record<string, string> = {
        practitioner: `Practitioner/${profile.id}`,
        organization: `Organization/${CONFIG.VITE_BENCHMARK_ORGANIZATION_ID}`,
        active: "true",
        _count: QUERY_LIMIT_SINGLE.toString(),
        _sort: "-_lastUpdated",
      };

      if (benchmarkOrgId) {
        searchParams.organization = `Organization/${benchmarkOrgId}`;
      }

      const roles = await medplum.searchResources(
        "PractitionerRole",
        searchParams,
      );
      setPractitionerRole(roles[0]);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    if (!enabled) return;
    queueMicrotask(() => {
      void fetchRole();
    });
  }, [enabled, fetchRole]);

  return {
    profile,
    practitionerRole,
    isLoading,
    error,
    refetch: fetchRole,
  };
}
